import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { TASK_INBOX_SNAPSHOT } from '../graphql/queries';

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes throttle
const DEBOUNCE_MS = 400;
const KNOWN_MAX = 300;
const KNOWN_TRIM = 100;

interface TaskInboxItem {
  taskId: string;
  taskTitle: string | null;
  taskStatus: string | null;
  taskUpdatedAt: string | null;
}

interface TaskInboxSnapshot {
  asAssigneeInProgress: TaskInboxItem[];
  asInitiatorCompleted: TaskInboxItem[];
}

export interface InboxBanner {
  taskId: string;
  text: string;
  key: string; // "A:<taskId>" for assignee, "I:<taskId>" for initiator
}

function knownStorageKey(userId: string): string {
  return `taskInbox_${userId}`;
}

function loadKnown(userId: string): Record<string, true> {
  try {
    return JSON.parse(localStorage.getItem(knownStorageKey(userId)) || '{}') as Record<string, true>;
  } catch {
    return {};
  }
}

function saveKnown(userId: string, known: Record<string, true>): void {
  const keys = Object.keys(known);
  if (keys.length > KNOWN_MAX) {
    keys.slice(0, KNOWN_TRIM).forEach((k) => delete known[k]);
  }
  try {
    localStorage.setItem(knownStorageKey(userId), JSON.stringify(known));
  } catch {
    // ignore quota errors
  }
}

function buildNewItems(snapshot: TaskInboxSnapshot, known: Record<string, true>): InboxBanner[] {
  const items: InboxBanner[] = [];
  for (const r of snapshot.asAssigneeInProgress) {
    const key = `A:${r.taskId}`;
    if (!known[key]) {
      items.push({ taskId: r.taskId, text: `Nový přiřazený úkol: ${r.taskTitle ?? ''}`, key });
    }
  }
  for (const r of snapshot.asInitiatorCompleted) {
    const key = `I:${r.taskId}`;
    if (!known[key]) {
      items.push({ taskId: r.taskId, text: `Váš úkol byl dokončen: ${r.taskTitle ?? ''}`, key });
    }
  }
  return items;
}

export function useTaskInbox() {
  const apollo = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [items, setItems] = useState<InboxBanner[]>([]);

  const lastPollAt = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSnapshot = useRef<TaskInboxSnapshot | null>(null);

  const poll = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const now = Date.now();
    if (now - lastPollAt.current < POLL_INTERVAL_MS) return;
    lastPollAt.current = now;

    try {
      const { data } = await apollo.query<{ taskInboxSnapshot: TaskInboxSnapshot }>({
        query: TASK_INBOX_SNAPSHOT,
        variables: { input: { userId } },
        fetchPolicy: 'no-cache',
      });
      const snapshot = data?.taskInboxSnapshot;
      if (!snapshot) return;
      lastSnapshot.current = snapshot;
      setItems(buildNewItems(snapshot, loadKnown(userId)));
    } catch (e) {
      console.warn('[useTaskInbox] poll error', e);
    }
  }, [apollo]);

  const debouncedPoll = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void poll();
    }, DEBOUNCE_MS);
  }, [poll]);

  // Trigger on every route change (throttled to max 1× per 2 min)
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId || location.pathname === '/login') return;
    debouncedPoll();
  }, [location.pathname, debouncedPoll]);

  // When user opens /tasks — mark all assignee items as seen
  useEffect(() => {
    if (!location.pathname.startsWith('/tasks')) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const snapshot = lastSnapshot.current;
    if (!snapshot) return;

    const known = loadKnown(userId);
    let changed = false;
    for (const r of snapshot.asAssigneeInProgress) {
      const key = `A:${r.taskId}`;
      if (!known[key]) {
        known[key] = true;
        changed = true;
      }
    }
    if (changed) {
      saveKnown(userId, known);
      setItems(buildNewItems(snapshot, known));
    }
  }, [location.pathname]);

  // Dismiss a single banner item and persist to known
  const dismiss = useCallback((item: InboxBanner) => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const known = loadKnown(userId);
    known[item.key] = true;
    saveKnown(userId, known);
    setItems((prev) => prev.filter((i) => i.key !== item.key));
  }, []);

  // Open the task detail and dismiss banner
  const openTask = useCallback(
    (item: InboxBanner) => {
      dismiss(item);
      navigate(`/tasks/${item.taskId}`);
    },
    [dismiss, navigate],
  );

  // Clear inbox state on logout (call when userId is removed)
  const clearInbox = useCallback(() => {
    setItems([]);
    lastSnapshot.current = null;
    lastPollAt.current = 0;
  }, []);

  return {
    badgeCount: items.length,
    banner: items[0] ?? null,
    dismiss,
    openTask,
    clearInbox,
  };
}
